import * as _ from 'lodash'
import { IBusinessObject } from '../PA.Core/PA.Core.IF.BO'
import { IMessageProvider, IProgressProvider, MessageType } from '../PA.Core/PA.Core.IF.Basic'
import { PAdbContext } from '../PA.Core/PAdbContext'
import { IdentityProvider } from '../PA.Core/Basic/Identity'
import { MessageProvider } from '../PA.Core/Basic/Messages'
import { ProgressProvider } from '../PA.Core/Basic/Progress'
import {
    WB_DataMartRow,
    WB_MasterDataSetRow,
    WB_SalesOrgRow,
    WB_DataMartScopeCustomerRelationshipRow,
    WB_DataMartPlanRelationshipRow,
    WB_DataMartScopeCustomerRelationshipFilterRow,
    WB_DataMartScopeProductRelationshipRow,
    WB_DataMartScopeProductRelationshipFilterRow,
    WB_DataMartAggregationCustomerRelationshipRow,
    WB_DataMartRestatementCustomerRelationshipRow,
    WB_DataMartAggregationProductRelationshipRow,
    WB_DataMartRestatementProductRelationshipRow,
    WB_HierarchyRow
} from '../PA.Core/PAdbSchemaRows'
import { IwbDataMart } from '../PA.Core/PAdbSchemaRowsIF'
import {
    esWB_DataMart,
    esWB_MasterData_Set,
    esWB_SalesOrg,
    esWB_DataMart_Scope_Customer_Relationship,
    esWB_DataMart_Plan_Relationship,
    esWB_DataMart_Scope_Product_Relationship_Filter,
    esWB_Customer_Relationship,
    esWB_Customer_Tag_Relationship,
    esWB_Customer_SalesArea_Relationship,
    esWB_Customer_Role_Relationship,
    esWB_DataMart_Scope_Customer_Relationship_Filter,
    esWB_DataMart_Aggregation_Customer_Relationship,
    esWB_DataMart_Aggregation_Product_Relationship,
    esWB_DataMart_Restatement_Customer_Relationship,
    esWB_DataMart_Restatement_Product_Relationship,
    esWB_DataMart_Scope_Product_Relationship,
    esWB_Plan,
    esWB_Hierarchy
} from '../PA.Core/PAdbSchema'
import { ESDataStore } from '../PA.Core/lib/ESDataStore'
import { ESRecord } from '../PA.Core/lib/ESRecord'
import { CreateOptions, ESRecordResolver } from '../PA.Core/lib/ESRecordResolver'
import { ASync } from '../PA.Core/Basic/ASync'
import { PAEnvironment } from '../PA.Core/PAEnvironment'
import { MDUsageService } from '../PA.MD.Services/MDUsageService'

export class DeconstructedDataMartBO implements IBusinessObject {
    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider

    CreateOptions: any

    LoadOptions: any
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: IMessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    dbContext: PAdbContext
    drWB_DataMart: WB_DataMartRow

    constructor(options?: any) {
        this.CreateOptions = options || {}

        if ('dbContext' in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext

        this.Identity = new IdentityProvider({ id: 0, name: 'New DataMart' })

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._OnLoadComplete = undefined
        this.LoadProgressProvider = new ProgressProvider('Loading', this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider('Validating', this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider('Saving', this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this)

        this.drWB_DataMart = null
    }

    get WB_DataMart(): WB_DataMartRow {
        return this.drWB_DataMart
    }

    //</editor-fold>

    //<editor-fold desc="IBusinessObject" operations>

    async LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this
        this.LoadProgressProvider.start('Creating Deconstructed DataMart')
        let internalOnComplete = (self._OnLoadComplete = (async () => {
            try {
                self.LoadOptions = options || {}
                if ('dbContext' in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext
                else self.dbContext = new PAdbContext()

                if (this.dbContext == null) {
                    let sError = 'Unable to determine dbContext for new DataMartBO'
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.exception(sError)
                    return self
                }

                //Create record using dbContext
                let newDeconstructedDataMartRow: WB_DataMartRow = <WB_DataMartRow>(
                    await self.dbContext.createRecordWithKey(esWB_DataMart)
                )

                newDeconstructedDataMartRow.setDefaults()
                self.drWB_DataMart = newDeconstructedDataMartRow
                //add this to all the BOs
                self.dbContext.add(esWB_DataMart, newDeconstructedDataMartRow)
                self.Identity.setAll(
                    self.WB_DataMart.cif.WB_DATAMART_ID,
                    self.WB_DataMart.cif.DM_ID,
                    self.WB_DataMart.cif.DM_NAME
                )
                self.LoadProgressProvider.setName('Loading ' + self.Identity.name + '(' + self.Identity.id_ui + ')')
                self.Identity.listenToRecordUpdates(self.WB_DataMart, 'WB_DATAMART_ID', 'DM_ID', 'DM_NAME')

                self.LoadProgressProvider.finish()
                self._bIsLoaded = true
                self._OnLoadComplete = null
                await self.applyCreateOptions()
                self.Identity.setAll(
                    self.WB_DataMart.cif.WB_DATAMART_ID,
                    self.WB_DataMart.cif.DM_ID,
                    self.WB_DataMart.cif.DM_NAME
                )

                return self
            } catch (err) {
                self.LoadProgressProvider.exception(err)
                return self
            }
        })())

        return internalOnComplete
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId)) self.WB_DataMart.cif.DM_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName)) self.WB_DataMart.cif.DM_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_DataMart.WB_MDSetRow = mds
                self.WB_DataMart.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Sales Org
        let wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId
        if (_.isNumber(wbSalesOrg)) {
            let salesOrg: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, wbSalesOrg)
            if (salesOrg) {
                self.WB_DataMart.WB_SalesOrgRow = salesOrg
                self.WB_DataMart.cif.WB_SALESORG_ID = wbSalesOrg
            }
        }

        //self.WB_DataMart.cif.DM_TYPE = 'Deconstructed'
        self.WB_DataMart.cif.PLAN_SCOPE_MANUAL_IND = '0'
    }

    async Load(anId: number | number, options?: any): Promise<IBusinessObject> {
        const self = this

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start('Loading (' + anId + ')')

        self._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    let sError = 'DataMart already loaded'
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.exception(sError)
                    return self
                }

                self.LoadOptions = options || {}
                if ('dbContext' in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext
                else if (self.dbContext == null) self.dbContext = new PAdbContext()

                if (this.dbContext == null) {
                    let sError = 'Unable to determine dbContext for new DataMart'
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.exception(sError)
                    return self
                }

                //Added for record resolver
                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver
                    recordResolverLoadPromise = ASync.resolve()
                } else {
                    recordResolver = DeconstructedDataMartBO.createRecordResolver({ dbContext: self.dbContext })
                    recordResolverLoadPromise = DeconstructedDataMartBO.setupRecordResolver(recordResolver, [anId])
                }

                await recordResolverLoadPromise
                let aDataMartRow: any = recordResolver.get(esWB_DataMart, anId)

                if (_.isEmpty(aDataMartRow)) {
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.exception('Id not found.')
                    return self
                }

                self.drWB_DataMart = aDataMartRow
                self.Identity.setAll(
                    self.WB_DataMart.cif.WB_DATAMART_ID,
                    self.WB_DataMart.cif.DM_ID,
                    self.WB_DataMart.cif.DM_NAME
                )
                self.LoadProgressProvider.setName('Loading ' + self.Identity.name + '(' + self.Identity.id_ui + ')')
                self.Identity.listenToRecordUpdates(self.WB_DataMart, 'WB_DATAMART_ID', 'DM_ID', 'DM_NAME')
                if (_.isNumber(self.drWB_DataMart.cif.WB_DATAMART_ID) && self.drWB_DataMart.cif.WB_DATAMART_ID > 0) {
                    let mruService = new MDUsageService()
                    await mruService.saveDataMartUsage(self.drWB_DataMart.cif.WB_DATAMART_ID)
                }
                self._bIsLoaded = true
                self._OnLoadComplete = null
                self.LoadProgressProvider.finish()
                return self
            } catch (err) {
                self._OnLoadComplete = null
                self.LoadProgressProvider.exception(err)
                return self
            }
        })()
        return this._OnLoadComplete
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this
        self.ValidateMessageProvider.clearMessages()
        self.ValidateProgressProvider.start(
            'Validating ' + self.WB_DataMart.cif.DM_NAME + '(' + self.WB_DataMart.cif.DM_ID + ')'
        )

        // Does no validations yet
        self.ValidateOptions = options || {}
        self._bIsValidated = true
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this
        const wbDataMart = <IwbDataMart>self.drWB_DataMart
        this.SaveProgressProvider.start('Saving ' + wbDataMart.DM_NAME + '(' + wbDataMart.DM_ID + ')')

        try {
            if (self.dbContext == null) {
                self._bIsSaved = false
                const sError: string = 'Unable to determine dbContext to Save DataMart.'
                self.SaveProgressProvider.exception(sError)
                return self
            }

            self.SaveOptions = options || {}

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any
            ) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            await self.Validate({})
            if (!self.bIsValidated) {
                self._bIsSaved = false
                self.SaveProgressProvider.finish()
                return self
            }

            wbDataMart.DM_TYPE = wbDataMart.PLAN_SCOPE_MANUAL_IND === '1' ? 'Manual' : 'By Year'

            const tasks: Promise<any>[] = []
            tasks.push(saveFunction(esWB_DataMart, self.WB_DataMart, {}))
            tasks.push(
                saveFunction(
                    esWB_DataMart_Scope_Customer_Relationship,
                    self.WB_DataMart.WB_DataMartScopeCustomerRelationshipRows,
                    {}
                )
            )
            /*
            tasks.push(
                saveFunction(
                    esWB_DataMart_Scope_Product_Relationship,
                    self.WB_DataMart.WB_DataMartScopeProductRelationshipRows,
                    {}
                )
            )
            tasks.push(
                saveFunction(
                    esWB_DataMart_Aggregation_Customer_Relationship,
                    self.WB_DataMart.WB_DataMartAggregationCustomerRelationshipRows,
                    {}
                )
            )
            tasks.push(
                saveFunction(
                    esWB_DataMart_Restatement_Customer_Relationship,
                    self.WB_DataMart.WB_DataMartRestatementCustomerRelationshipRows,
                    {}
                )
            )
            tasks.push(
                saveFunction(
                    esWB_DataMart_Aggregation_Product_Relationship,
                    self.WB_DataMart.WB_DataMartAggregationProductRelationshipRows,
                    {}
                )
            )
            tasks.push(
                saveFunction(
                    esWB_DataMart_Restatement_Product_Relationship,
                    self.WB_DataMart.WB_DataMartRestatementProductRelationshipRows,
                    {}
                )
            )

            //   tasks.push(saveFunction(esWB_DataMart_Plan_Relationship, self.WB_DataMart.WB_DataMartPlanRelationshipRows, {}));
            await Promise.all(tasks)
*/
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true
                })
            } else {
                self._bIsSaved = true
            }
            self.SaveProgressProvider.finish()
        } catch (err) {
            self.SaveProgressProvider.exception(err)
        }

        return self
    }

    get bIsSaved(): boolean {
        return this._bIsSaved
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this
        const wbDataMart = <IwbDataMart>self.drWB_DataMart
        this.DeleteMessageProvider.clearMessages()
        this.DeleteProgressProvider.start('Deleting ' + wbDataMart.DM_NAME + '(' + wbDataMart.DM_ID + ')')

        try {
            // 1. Validate if we can delete the DataMart Source

            // 2. If Valid, mark all the records for deletion.
            if (self.DeleteMessageProvider.countMessages(MessageType.Error) > 0) {
                // Don't delete..
                this.DeleteProgressProvider.finish()
                return self
            }

            self.drWB_DataMart.markDeleted()

            //          _.forEach(self.drWB_DataMart.WB_DataMartPlanRelationshipRows, (row) => {
            //              if (row.isActive) {
            //row.markDeleted();
            //   }
            //});

            if (bSaveNow) {
                await self.SaveUsing(false)
            }

            self._bIsDeleted = true
            self.DeleteProgressProvider.finish()
        } catch (err) {
            this.DeleteProgressProvider.exception(err)
        }

        return self
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

    async addCustomerScope(CustomerHierarchy: number): Promise<WB_DataMartScopeCustomerRelationshipRow> {
        const self = this

        let found = false
        _.forEach(self.WB_DataMart.WB_DataMartScopeCustomerRelationshipRows, (aCustomerHierarchy) => {
            if (aCustomerHierarchy.cif.WB_CUSTH_ID == CustomerHierarchy && aCustomerHierarchy.isActive) found = true
        })
        if (found) return

        let aNewRow: WB_DataMartScopeCustomerRelationshipRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_DataMart_Scope_Customer_Relationship)
        )
        aNewRow.setDefaults()
        aNewRow.cif.WB_DATAMART_ID = self.drWB_DataMart.cif.WB_DATAMART_ID
        aNewRow.cif.WB_CUSTH_ID = CustomerHierarchy
        aNewRow.WB_DataMartRow = self.drWB_DataMart
        //       aNewRow.WB_DataMartScopeCustomerRelationshipFilterRows = test
        self.dbContext.add(esWB_DataMart_Scope_Customer_Relationship, aNewRow)

        return aNewRow
    }

    async removeCustomerScope(CHR: number): Promise<void> {
        const self = this

        _.forEach(self.WB_DataMart.WB_DataMartScopeCustomerRelationshipRows, (aCustomerR) => {
            if (aCustomerR.cif.WB_DATAMARTCHR_ID == CHR && aCustomerR.isActive) aCustomerR.markDeleted()
        })
    }

    //</editor-fold>

    //<editor-fold desc="IRecordResolver" operations>

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext
        } else {
            dbContext = new PAdbContext({})
        }

        const dataMart_options: CreateOptions = {
            standardConditions: {
                [esWB_DataMart_Plan_Relationship]: { DELETED_IND: { neq: '1' } },
                [esWB_DataMart_Aggregation_Customer_Relationship]: { DELETED_IND: { notIn: ['1'] } },
                [esWB_DataMart_Aggregation_Product_Relationship]: { DELETED_IND: { notIn: ['1'] } },
                [esWB_DataMart_Restatement_Customer_Relationship]: { DELETED_IND: { notIn: ['1'] } },
                [esWB_DataMart_Restatement_Product_Relationship]: { DELETED_IND: { notIn: ['1'] } },
                [esWB_DataMart_Scope_Product_Relationship]: { DELETED_IND: { notIn: ['1'] } },
                [esWB_DataMart_Scope_Customer_Relationship]: { DELETED_IND: { notIn: ['1'] } },
                [esWB_Hierarchy]: { DELETED_IND: { notIn: ['1'] } }
            },
            dctIncludeBelongsToNamedRelations: {}
        }

        const aRecResolver = new ESRecordResolver(dbContext, dataMart_options)
        return aRecResolver
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver
                .findAllWithRelationships(esWB_DataMart, ids)
                .then((result) => {
                    resolve()
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }

    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass(DeconstructedDataMartBO, 'DeconstructedDataMartBO', 'Deconstructed DataMart')
